<template>
  <div class="p-2 shadow dark:bg-charcoal-900 gap-2 flex flex-col">
    <u-button block icon="i-ri-save-3-line" @click="saveTemplate()">Save Current as Template</u-button>
    <u-button v-if="isAdmin" block icon="i-ri-save-3-line" @click="saveTemplate({type: SURFACE_EDITOR_TEMPLATE_TYPES.system})">
      Save Current as Curated Template
    </u-button>
  </div>
  <div class="flex-1 overflow-y-auto scrollbar-light dark:scrollbar-dark scrollbar-gutter-stable p-3">
    <section v-if="systemTemplates?.length" class="mb-4">
      <surface-editor-core-panel-section-heading>Curated Templates</surface-editor-core-panel-section-heading>
      <div class="grid grid-cols-2 gap-3">
        <surface-editor-tabs-template-button
          v-for="template in systemTemplates"
          :key="template.id"
          :template="getDisplayTemplateSurface(template.body)"
          :can-delete="isAdmin"
          draggable="true"
          @dragstart="event => onDragStart(event, template)"
          @dragend="onDragEnd"
          @apply="applyTemplate(template)"
          @delete="deleteTemplate(template)"
        />
      </div>
    </section>

    <section v-if="userTemplates?.length">
      <surface-editor-core-panel-section-heading>Your Saved Templates</surface-editor-core-panel-section-heading>

      <div class="grid grid-cols-2 gap-3">
        <surface-editor-tabs-template-button
          v-for="template in userTemplates"
          :key="template.id"
          :template="getDisplayTemplateSurface(template.body)"
          draggable="true"
          @dragstart="event => onDragStart(event, template)"
          @dragend="onDragEnd"
          @apply="applyTemplate(template)"
          @delete="deleteTemplate(template)"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
  import cloneDeep from 'lodash.clonedeep';
  import SurfaceEditorConfirmTemplateVariableSaveModal from '~/components/surface-editor/confirm-template-variable-save-modal.vue';
  import {resolveSurfaceEditorTemplateText} from '~/composables/surface-editor/template-dynamic-text.js';
  import {storeToRefs} from 'pinia';

  const surfaceEditorStore = useSurfaceEditorStore();
  const sessionStore = useSessionStore();

  const {templates, activeSurface, conf} = storeToRefs(surfaceEditorStore);
  const {setDragPayload, clearDragPayload} = useSurfaceEditorDragState();

  const isAdmin = computed(() => sessionStore.currentUser?.can.adminDesignEditor);
  const designProjectType = computed(() => conf.value?.designProjectType || null);

  useAsyncData(
    'surface-editor-templates',
    () => surfaceEditorStore.getTemplates({designProjectType: designProjectType.value})
  );

  watch(designProjectType, (newType) => {
    surfaceEditorStore.getTemplates({designProjectType: newType});
  });

  function matchesDesignProjectType(template) {
    if (!designProjectType.value) {
      return true;
    }

    return template.design_project_type === designProjectType.value;
  }

  const systemTemplates = computed(() => (
    templates.value?.[SURFACE_EDITOR_TEMPLATE_TYPES.system]?.filter(matchesDesignProjectType)
  ));
  const userTemplates = computed(() => (
    templates.value?.[SURFACE_EDITOR_TEMPLATE_TYPES.user]?.filter(matchesDesignProjectType)
  ));
  const dynamicTextData = computed(() => conf.value?.dynamicTextData);

  function onDragStart(event, template) {
    const dataTransfer = event.dataTransfer;

    if (!dataTransfer) {
      return;
    }

    const payload = {
      type: 'template',
      id: template.id,
      body: template.body
    };

    dataTransfer.setData('application/json', JSON.stringify(payload));
    dataTransfer.setData('text/plain', template.id);
    dataTransfer.effectAllowed = 'copy';
    setDragPayload(payload);
  }

  function getDisplayTemplateSurface(templateBody) {
    return resolveSurfaceEditorTemplateText({
      surface: templateBody,
      data: dynamicTextData.value
    });
  }

  function onDragEnd() {
    clearDragPayload();
  }

  async function saveTemplate({type = SURFACE_EDITOR_TEMPLATE_TYPES.user} = {}) {
    try {
      const surface = activeSurface.value;

      if (!surface) {
        return;
      }

      const variableTextElements = getVariableTemplateTextElements(surface);

      if (!variableTextElements.length) {
        await persistTemplate({surface, type});
        return;
      }

      const confirm = useFModal({
        size: 'sm',
        showClose: false,
        clickToClose: false,
        escToClose: false,
        component: SurfaceEditorConfirmTemplateVariableSaveModal,
        props: {
          message: buildVariableTemplateConfirmMessage(variableTextElements)
        },
        eventHandlers: {
          async makeDynamic() {
            try {
              await persistTemplate({surface, type, populateTemplateText: true});
              await this.close();
            } catch (e) {
              useErrorToast().add();
            }
          },
          async saveAsIs() {
            try {
              await persistTemplate({surface, type});
              await this.close();
            } catch (e) {
              useErrorToast().add();
            }
          }
        },
      });

      await confirm.open();
    } catch (e) {
      useErrorToast().add();
    }
  }

  async function persistTemplate({surface, type, populateTemplateText = false}) {
    await surfaceEditorStore.saveAsTemplate({
      surface: buildTemplateSurfaceForSave({surface, populateTemplateText}),
      type,
      designProjectType: designProjectType.value
    });
  }

  function buildTemplateSurfaceForSave({surface, populateTemplateText}) {
    const surfaceToSave = cloneDeep(surface);

    if (!populateTemplateText) {
      return surfaceToSave;
    }

    for (const element of surfaceToSave.elements || []) {
      if (element?.type !== 'text_box') {
        continue;
      }

      const text = element?.content?.text;

      if (typeof text !== 'string' || !containsTemplatePlaceholder(text)) {
        continue;
      }

      element.content.template = text;
      element.content.text = '';
    }

    return surfaceToSave;
  }

  function getVariableTemplateTextElements(surface) {
    return (surface?.elements || [])
      .filter(element => (
        element?.type === 'text_box'
        && typeof element?.content?.text === 'string'
        && containsTemplatePlaceholder(element.content.text)
      ))
      .map(element => ({
        preview: getTextPreview(element.content.text)
      }));
  }

  function containsTemplatePlaceholder(text) {
    return /\{\{[\s\S]+?\}\}/.test(text);
  }

  function getTextPreview(text) {
    const normalized = text.replace(/\s+/g, ' ').trim();
    return normalized.length > 60 ? `${normalized.slice(0, 57)}...` : normalized;
  }

  function escapeHtml(text) {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll('\'', '&#39;');
  }

  function buildVariableTemplateConfirmMessage(elements) {
    const previews = elements
      .slice(0, 5)
      .map((element, index) => `${index + 1}. <code>${escapeHtml(element.preview || '(empty)')}</code>`)
      .join('<br>');

    const overflowCount = Math.max(0, elements.length - 5);

    return [
      'These text elements may contain variables. Do you want to save them as dynamically populated text elements?',
      `<br><br>${previews}`,
      overflowCount ? `<br>+ ${overflowCount} more` : ''
    ].join('');
  }

  async function applyTemplate(template) {
    try {
      await surfaceEditorStore.applyTemplate({
        template: template.body,
        surface: activeSurface.value
      });
    } catch (e) {
      useErrorToast().add();
    }
  }

  async function deleteTemplate(template) {
    useConfirmModal({
      heading: `Delete Template?`,
      message: `This template will be removed and cannot be recovered.`,
      confirmText: 'Delete',
      confirmMethod: async () => {
        try {
          await surfaceEditorStore.deleteTemplate({template: template});
        } catch (e) {
          useErrorToast().add();
        }
      }
    }).open();
  }

</script>
